import * as React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Home } from '@app/Home/Home';
import { Support } from '@app/Support/Support';
import { Error } from '@app/Error/Error';
import { EntraOffboarding } from '@app/Offboarding/EntraOffboarding';
import { DefenderOffboarding } from '@app/Offboarding/DefenderOffboarding';
import { AzureOffboarding } from '@app/Offboarding/AzureOffboarding';
import { summary } from '@app/Findings/summary';
import { environmentFindings } from '@app/Findings/environmentFindings';
import { assetFindings } from '@app/Findings/assetFindings';
import { accountFindings } from '@app/Findings/accountFindings';
import { accounts } from '@app/Findings/accounts';
import { assets } from '@app/Findings/assets';
//import { GeneralSettings } from '@app/Settings/General/GeneralSettings';
//import { ProfileSettings } from '@app/Settings/Profile/ProfileSettings';
import { NotFound } from '@app/NotFound/NotFound';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';
import { PARTNER_ID, PRODUCT_NAME } from '../config/partner';
import { Reports } from '@app/Reports/Reports';
import { ProtectedRoute } from '@app/utils/ProtectedRoute';
import { AuthCallback } from '@app/AuthCallback/AuthCallback';
import { Unauthorized } from '@app/Unauthorized/Unauthorized';
import { Terms } from '@app/Terms/Terms';
import { AgentConfigDemo } from '@app/AgentConfigDemo/AgentConfigDemo';

let routeFocusTimer: number;
export interface IAppRoute {
  label?: string; // Excluding the label will exclude the route from the nav sidebar in AppLayout
  component: React.ComponentType;
  path: string;
  title: string;
  routes?: undefined;
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

const routes: AppRouteConfig[] = [
  {
    component: Home,
    label: 'Home',
    path: '/',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Home`,
  },
  {
    component: Support,
    label: 'Documentation',
    path: '/docs',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Docs`,
  },
  {
    component: Terms,
    path: '/terms',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Terms and Conditions`,
  },
  {
    component: EntraOffboarding,
    path: '/offboarding/entra',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Entra Offboarding`,
  },
  {
    component: DefenderOffboarding,
    path: '/offboarding/defender',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Defender Offboarding`,
  },
  {
    component: AzureOffboarding,
    path: '/offboarding/azure',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Azure Offboarding`,
  },
  {
    component: Error,
    path: '/error',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | ERROR`,
  },
  {
    component: Unauthorized,
    path: '/unauthorized',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Access Denied`,
  },
  /*{
    component: Reports,
    label: 'Reports',
    path: '/reports',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Reports`,
  },*/
  {
    component: AuthCallback,
    path: '/auth/callback',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Authentication`,
  },
  {
    component: AgentConfigDemo,
    path: '/agentConfigDemo',
    title: `${PARTNER_ID} ${PRODUCT_NAME} | Agent Configuration Demo`,
  },
  {
    label: 'Example Findings',
    routes: [
      {
        component: summary,
        label: 'Summary',
        path: '/findings/summary',
        title: `${PARTNER_ID} ${PRODUCT_NAME} | Findings Summary`,
      },
      {
        component: environmentFindings,
        label: 'Environment Findings',
        path: '/findings/environmentFindings',
        title: `${PARTNER_ID} ${PRODUCT_NAME} | Environment Findings`,
      },
      {
        component: assetFindings,
        label: 'Asset Findings',
        path: '/findings/assetFindings',
        title: `${PARTNER_ID} ${PRODUCT_NAME} | Asset Findings`,
      },
      {
        component: accountFindings,
        label: 'Account Findings',
        path: '/findings/accountFindings',
        title: `${PARTNER_ID} ${PRODUCT_NAME} | Account Findings`,
      },
      {
        component: accounts,
        label: 'Active Accounts',
        path: '/findings/accounts',
        title: `${PARTNER_ID} ${PRODUCT_NAME} | Accounts`,
      },
      {
        component: assets,
        label: 'Detected Assets',
        path: '/findings/assets',
        title: `${PARTNER_ID} ${PRODUCT_NAME} | Assets`,
      }
    ]
  }
];

// a custom hook for sending focus to the primary content container
// after a view has loaded so that subsequent press of tab key
// sends focus directly to relevant content
const useA11yRouteChange = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    routeFocusTimer = window.setTimeout(() => {
      const mainContainer = document.getElementById('primary-app-container');
      if (mainContainer) {
        mainContainer.focus();
      }
    }, 50);
    return () => {
      window.clearTimeout(routeFocusTimer);
    };
  }, [pathname]);
};

const RouteWithTitleUpdates = ({ component: Component, title, path }: IAppRoute) => {
  useA11yRouteChange();
  useDocumentTitle(title);

  return <Component />;
};

const PageNotFound = ({ title }: { title: string }) => {
  useDocumentTitle(title);
  return <NotFound />;
};

const flattenedRoutes: IAppRoute[] = routes.reduce(
  (flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])],
  [] as IAppRoute[],
);

const AppRoutes = (): React.ReactElement => (
  <Routes>
    {flattenedRoutes.map(({ path, component, title }, idx) => {
      if (path === '/reports') {
        return (
          <Route
            key={idx}
            path={path}
            element={
              <ProtectedRoute component={component} />
            }
          />
        );
      }
      return (
        <Route
          key={idx}
          path={path}
          element={
            <RouteWithTitleUpdates
              path={path}
              component={component}
              title={title}
            />
          }
        />
      );
    })}
    <Route path="*" element={<PageNotFound title="404 Page Not Found" />} />
  </Routes>
);

export { AppRoutes, routes };
